import React from 'react';
import PropTypes from 'prop-types';

import { Row, Col, Dropdown, DropdownButton, Card } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faPhone, faEllipsisV } from '@fortawesome/pro-solid-svg-icons';

import confirm from 'components/confirm';

export default class SurveyProperty extends React.Component {

  static propTypes = {
    projectId: PropTypes.number,
    property: PropTypes.object.isRequired,
    removeSurveyProperty: PropTypes.func.isRequired,
    toggleReject: PropTypes.func.isRequired
  }

  moveLinkText = () => {
    return this.props.property.rejected ? 'Re-Add' : 'Reject';
  }

  phoneNumberForContact = (contact) => {
    if (contact.phone) {
      return contact.phoneFormatted;
    }
    return (
      <span className="text-muted">---</span>
    );
  }

  handleToggleReject = (e) => {
    e.preventDefault();
    this.props.toggleReject(this.props.property.updatePath, this.props.property);
  }

  confirmDelete = () => {
    const { projectId, property, removeSurveyProperty } = this.props;
    const message = "Are you sure you want to remove this lead?";
    confirm(message, {
      buttonLabel: 'Remove',
      buttonStyle: 'danger',
      title: 'Delete Lead'
    }).then(() => {
      removeSurveyProperty(projectId, property);
    }).catch(() => {});
  }

  renderIndexAttributes() {
    const attributes = this.props.property.indexAttributes;
    const attributeJSX = attributes.map((attr) => {
      const valueClass = attr.blank ? "text-muted" : "";
      return (
        <span className='index-attribute py-3' key={attr.label}>
          <span className='text-muted me-2'>
            {attr.label}
          </span>
          <span className={valueClass}>
            {this.renderValue(attr)}
          </span>
        </span>
      );
    });

    return <div className="d-flex gap-8 align-items-center">{attributeJSX}</div>;
  }

  renderInfoAttributes(attributes) {
    const attributeJSX = attributes.map((attr) => {
      const valueClass = attr.blank ? "text-muted" : "";
      return (
        <div className='py-1' key={attr.label}>
          <span className='text-muted mr-2'>
            {attr.label}
          </span>
          <span className={valueClass}>
            {this.renderValue(attr)}
          </span>
        </div>
      );
    });

    return <div>{attributeJSX}</div>;
  }

  renderValue(attr) {
    if (attr.blank) {
      return "---";
    }
    return attr.value;
  }

  renderDataTable() {
    const property = this.props.property;
    const lease = property.leaseDetails;
    return (
      <table className='w-100 mt-5'>
        <thead>
          <tr>
            <th className='w-50 pb-2'>Building Information</th>
            {lease &&
              <th className='w-50 pb-2'>Lease Space Overview</th>
            }
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className='py-1 pr-3' valign="top">
              {this.renderInfoAttributes(property.buildingInfoAttributes)}
            </td>
            {lease &&
              <td className='py-1' valign="top">
                {this.renderInfoAttributes(lease.leaseInfoAttributes)}
              </td>
            }
          </tr>
        </tbody>
      </table>
    );
  }

  renderContactRow(contactRole, size = 'regular') {
    const contact = contactRole.contact;
    const [WrapperDivTag, ItemDivTag] = (size === 'small') ? ['div', 'div'] : ['tr', 'td'];
    const wrapperClass = (size === 'small') ? 'pb-3' : '';
    const className = (size === 'small') ? '' : 'w-25';
    return (
      <WrapperDivTag className={wrapperClass} key={contactRole.id} name="contactRow">
        <ItemDivTag className={`py-1 ${className}`}>{contact.name}</ItemDivTag>
        <ItemDivTag className={`py-1 ${className}`}>
          <FontAwesomeIcon className='me-2' icon={faEnvelope} />
          {contact.email}
        </ItemDivTag>
        <ItemDivTag className={`py-1 ${className}`}>
          <FontAwesomeIcon className='me-2' icon={faPhone} />
          {this.phoneNumberForContact(contact)}
        </ItemDivTag>
        <ItemDivTag className={`py-1 ${className}`}>
          <span className='text-muted'>Relation:</span> {contactRole.relationship}
        </ItemDivTag>
      </WrapperDivTag>
    );
  }

  renderContactsTable() {
    const contactRoles = this.props.property.contactRoles;
    if (contactRoles.length < 1) return;

    return (
      <table className='w-100 mt-5' name="contactTable">
        <thead>
          <tr>
            <th className='pb-2' colSpan='4'>Location Contacts</th>
          </tr>
        </thead>
        <tbody>
          {contactRoles.map((contactRole) => {
            return this.renderContactRow(contactRole);
          })}
        </tbody>
      </table>
    );
  }

  renderSmallContactsTable() {
    const contactRoles = this.props.property.contactRoles;
    if (contactRoles.length < 1) return;

    return (
      <div className='mt-5'>
        <div className='pb-2'><strong>Location Contacts</strong></div>
        {contactRoles.map((contactRole) => {
          return this.renderContactRow(contactRole, 'small');
        })}
      </div>
    );
  }

  displayName() {
    const property = this.props.property;
    if (property.leaseDetails && property.leaseDetails.suite) {
      return `${property.researchProperty.displayName} - ${property.leaseDetails.suite}`;
    }
    return property.researchProperty.displayName;
  }

  renderSurveyPropertyOptions() {
    const { property } = this.props;
    return (
      <DropdownButton title={<FontAwesomeIcon icon={faEllipsisV} />} variant="ghost btn-icon">
        <Dropdown.Item
          eventKey="1"
          href='#'
          name={`toggle-reject-${property.id}`}
          onClick={this.handleToggleReject}
        >
          {this.moveLinkText()}
        </Dropdown.Item>
        <Dropdown.Item
          eventKey="2"
          href={this.props.property.editPath}
          name={`edit-survey-property-${property.id}`}
        >
          Edit
        </Dropdown.Item>
        <Dropdown.Item
          eventKey="3"
          name={`delete-survey-property-${property.id}`}
          onClick={this.confirmDelete}
        >
          Delete
        </Dropdown.Item>
      </DropdownButton>
    );
  }

  render() {
    const property = this.props.property;
    const researchProperty = property.researchProperty;

    return (
      <Card>
        <Card.Body>
          <Row className="mb-4 align-items-center">
            <Col>
              <strong>{researchProperty.addressTwoLine[0]} </strong>
              <span>{researchProperty.addressTwoLine[1]} </span>
            </Col>
            <Col xs="auto">
              {this.renderSurveyPropertyOptions()}
            </Col>
          </Row>
          <div className="d-none d-md-block">
            <Row className='row-eq-height align-top'>
              <Col sm={2} xs={3}>
                <img className='img-fluid survey-image' src={property.photoUrl} />
              </Col>
              <Col>
                <div className='gradient-cover'/>
                <div>
                  {this.renderIndexAttributes()}
                  <p className='mt-4'>
                    {property.description}
                  </p>
                  {this.renderDataTable()}
                  {this.renderContactsTable()}
                </div>
              </Col>
            </Row>
          </div>
          <div className="align-top d-md-none">
            <div>
              <div className='gradient-cover'/>
              <Row>
                <Col xs={6}>
                  <img className='img-responsive survey-image' src={property.photoUrl} />
                </Col>
                <Col xs={6}>
                  {this.renderInfoAttributes(this.props.property.indexAttributes)}
                </Col>
              </Row>
              <div><p className='mt-4'>{property.description}</p></div>
              {this.renderDataTable()}
              {this.renderSmallContactsTable()}
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  }
}
